import React from "react";

const AboutUs = () => {
  return (
    <section className="bg-black text-white py-16 px-8" id="about-us">
      <div className="container mx-auto text-center">
        <h2 className="text-4xl font-bold mb-8 animate-fade-in-down">
          About Us
        </h2>

        <div className="text-lg leading-relaxed animate-slide-in-right">
          {/* --- Existing About Us content --- */}
          <p className="mb-6">
            <span className="text-gray-300 font-bold">AlphaQ</span> is a
            <span className="font-bold text-gray-400"> global technology partner </span>
            driving innovation across high-precision industries including{" "}
            <span className="font-bold text-gray-400">
              Automobile, Aerospace, Defense & Military, Nuclear, Engineering,
              Plastic Extrusion, Paper & Paperboard, Metal, and Flexible
              Packaging
            </span>
            . For over three decades, we have empowered India’s most advanced
            sectors with precision measurement, control, and automation
            excellence.
          </p>
          <p className="mb-6">
            We bring world-renowned technologies for{" "}
            <span className="font-bold text-gray-400">
              online measurement and control solutions
            </span>
            , offering unmatched capabilities in{" "}
            <span className="font-bold text-gray-400">
              Web Defect Inspection (AOI), Gauging, Defect Tagging, and
              High-Performance Cast Film Lines
            </span>
            . Our 3D non-contact metrology and optical 3D profilometers deliver
            fully automated inspection systems, helping industries achieve
            next-level accuracy and productivity.
          </p>
          <p className="mb-6">
            Our expertise extends to{" "}
            <span className="font-bold text-gray-400">
              high-precision laser-based measurement systems
            </span>{" "}
            — automated, high-resolution, and built for mission-critical
            applications in{" "}
            <span className="font-bold text-gray-400">
              Defense, Military (Army, Navy & Airforce), and Nuclear
            </span>{" "}
            domains. We design and integrate{" "}
            <span className="font-bold text-gray-400">
              Optoelectronic Devices, Laser Sensors, and Laser Profilers
            </span>{" "}
            that deliver accuracy even in the most demanding industrial
            environments.
          </p>
          <p className="mb-6">
            At AlphaQ, we provide{" "}
            <span className="font-bold text-gray-400">
              comprehensive control and automation solutions
            </span>{" "}
            — from intelligent sensors to multifunctional measuring and
            inspection systems — engineered for{" "}
            <span className="font-bold text-gray-400">
              Aerospace, Defense & Military, Nuclear, Automobile, and Space
            </span>{" "}
            applications. Our systems are capable of measuring{" "}
            <span className="font-bold text-gray-400">
              3D geometry (GD&T parameters), thickness, roughness, and defects
            </span>{" "}
            with micron-level precision.
          </p>
          <p className="mb-6">
            Designed for reliability in{" "}
            <span className="font-bold text-gray-400">
              hot, cold, radioactive, cryogenic, and vacuum environments
            </span>
            , our technologies ensure performance where others fail. Every
            product reflects our legacy of{" "}
            <span className="font-bold text-gray-400">
              Micron Precision, Measurement Accuracy, and Engineering Excellence
            </span>
            .
          </p>
          <p className="mb-6">
            With{" "}
            <span className="font-bold text-gray-400">35+ years of experience</span>{" "}
            in technical sales, R&D lab equipment, and in-line automated
            inspection systems, AlphaQ continues to redefine the standards of
            quality and innovation. Our mission is to empower industries to
            achieve higher efficiency, reliability, and technological supremacy
            through precision-driven solutions.
          </p>
        </div>

{/* --- INDUSTRIES & APPLICATIONS --- */}
<section className="bg-black text-white py-20 px-8">
  <h2 className="text-4xl font-bold text-center mb-10 bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent animate-fade-in">
    Industries & Applications
  </h2>

  <p className="text-center text-gray-300 max-w-3xl mx-auto mb-14 text-lg leading-relaxed">
    We deliver high-precision optical metrology and AI-driven inspection systems
    engineered for mission-critical applications across advanced industries.
  </p>

  <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">

    {/* Aerospace */}
    <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-black p-8 rounded-2xl border border-gray-700 shadow-xl transform hover:scale-105 transition-all duration-500 hover:shadow-[0_0_20px_rgba(255,255,255,0.1)]">
      <h3 className="text-2xl font-semibold mb-3 text-white tracking-wide">Aerospace</h3>
      <p className="text-gray-300 leading-relaxed mb-4">
        Precision-driven metrology ensuring flawless inspection of safety-critical components.
      </p>
      <ul className="text-gray-400 space-y-1 text-sm">
        <li>• Airfoil, blade, blisk & stator inspection</li>
        <li>• Fir-tree base & blade edge metrology</li>
        <li>• Cooling hole inspection (turbine blades)</li>
        <li>• Cryogenic fuel level measurement</li>
        <li>• Rivet hole & flushness analysis</li>
      </ul>
    </div>

    {/* Automotive */}
    <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-black p-8 rounded-2xl border border-gray-700 shadow-xl transform hover:scale-105 transition-all duration-500 hover:shadow-[0_0_20px_rgba(255,255,255,0.1)]">
      <h3 className="text-2xl font-semibold mb-3 text-white tracking-wide">Automotive</h3>
      <p className="text-gray-300 leading-relaxed mb-4">
        High-speed 3D inspection solutions for precision automotive manufacturing.
      </p>
      <ul className="text-gray-400 space-y-1 text-sm">
        <li>• Turbo shaft chatter measurement</li>
        <li>• Fuel injector nozzle inspection</li>
        <li>• Engine block flatness testing</li>
        <li>• Cylinder bore & tube inspection</li>
        <li>• Casting defect detection</li>
      </ul>
    </div>

    {/* Defense */}
    <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-black p-8 rounded-2xl border border-gray-700 shadow-xl transform hover:scale-105 transition-all duration-500 hover:shadow-[0_0_20px_rgba(255,255,255,0.1)]">
      <h3 className="text-2xl font-semibold mb-3 text-white tracking-wide">Defense & Military</h3>
      <p className="text-gray-300 leading-relaxed mb-4">
        Defense-grade inspection systems built for uncompromising accuracy and reliability.
      </p>
      <ul className="text-gray-400 space-y-1 text-sm">
        <li>• Small & large caliber barrel inspection</li>
        <li>• Rifle chamber & choke inspection</li>
        <li>• Ammunition geometry measurement</li>
        <li>• Firearm component 3D metrology</li>
      </ul>
    </div>

    {/* Semiconductor */}
    <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-black p-8 rounded-2xl border border-gray-700 shadow-xl transform hover:scale-105 transition-all duration-500 hover:shadow-[0_0_20px_rgba(255,255,255,0.1)]">
      <h3 className="text-2xl font-semibold mb-3 text-white tracking-wide">
        Semiconductor & Electronics
      </h3>
      <p className="text-gray-300 leading-relaxed mb-4">
        Micro-level inspection enabling yield optimization and process control.
      </p>
      <ul className="text-gray-400 space-y-1 text-sm">
        <li>• Wafer edge & coating inspection</li>
        <li>• Wafer warp & bow analysis</li>
        <li>• CMP pad measurement</li>
        <li>• Solder bump inspection</li>
        <li>• PCB & flexible board AOI</li>
      </ul>
    </div>

    {/* Biomedical */}
    <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-black p-8 rounded-2xl border border-gray-700 shadow-xl transform hover:scale-105 transition-all duration-500 hover:shadow-[0_0_20px_rgba(255,255,255,0.1)]">
      <h3 className="text-2xl font-semibold mb-3 text-white tracking-wide">
        Biomedical & Optics
      </h3>
      <p className="text-gray-300 leading-relaxed mb-4">
        Advanced surface and structural analysis for medical and optical components.
      </p>
      <ul className="text-gray-400 space-y-1 text-sm">
        <li>• Medical coating inspection</li>
        <li>• Biomedical chip inspection</li>
        <li>• Blood glucose strip inspection</li>
        <li>• Optical lens profiling</li>
        <li>• Glass thickness mapping</li>
      </ul>
    </div>

    {/* Energy */}
    <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-black p-8 rounded-2xl border border-gray-700 shadow-xl transform hover:scale-105 transition-all duration-500 hover:shadow-[0_0_20px_rgba(255,255,255,0.1)]">
      <h3 className="text-2xl font-semibold mb-3 text-white tracking-wide">
        Energy & Heavy Industry
      </h3>
      <p className="text-gray-300 leading-relaxed mb-4">
        Rugged inspection systems designed for harsh industrial environments.
      </p>
      <ul className="text-gray-400 space-y-1 text-sm">
        <li>• Turbine blade inspection</li>
        <li>• Solar panel coating measurement</li>
        <li>• Weld & alignment inspection</li>
        <li>• Tool wear & coating analysis</li>
        <li>• Inline thickness & coat-weight gauging</li>
      </ul>
    </div>

  </div>
</section>

{/* --- SPECIAL & ADVANCED APPLICATIONS --- */}
<section className="bg-black text-white py-24 px-8 border-t border-gray-800">

  {/* Application Grid */}
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">

    {/* Thin Film */}
    <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-black p-7 rounded-2xl border border-gray-700 hover:shadow-[0_0_20px_rgba(255,255,255,0.08)] transition-all duration-500">
      <h3 className="text-xl font-semibold mb-3">Thin Film Inspection</h3>
      <ul className="text-gray-400 text-sm space-y-1">
        <li>• Pinhole & stain inspection</li>
        <li>• Foreign matter & crystallite detection</li>
        <li>• Gel, fish-eye & black spot inspection</li>
      </ul>
    </div>

    {/* Non-woven */}
    <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-black p-7 rounded-2xl border border-gray-700 hover:shadow-[0_0_20px_rgba(255,255,255,0.08)] transition-all duration-500">
      <h3 className="text-xl font-semibold mb-3">Non-Woven Materials</h3>
      <ul className="text-gray-400 text-sm space-y-1">
        <li>• Stain & defect inspection</li>
        <li>• Spunbond, melt-blown, composite</li>
        <li>• Wet-laid, air-laid & needle-punched</li>
      </ul>
    </div>

    {/* Steel */}
    <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-black p-7 rounded-2xl border border-gray-700 hover:shadow-[0_0_20px_rgba(255,255,255,0.08)] transition-all duration-500">
      <h3 className="text-xl font-semibold mb-3">Steel & Metal</h3>
      <ul className="text-gray-400 text-sm space-y-1">
        <li>• Roller & surface inspection</li>
        <li>• CCD-based metal inspection</li>
      </ul>
    </div>

    {/* Paper */}
    <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-black p-7 rounded-2xl border border-gray-700 hover:shadow-[0_0_20px_rgba(255,255,255,0.08)] transition-all duration-500">
      <h3 className="text-xl font-semibold mb-3">Paper & Paperboard</h3>
      <ul className="text-gray-400 text-sm space-y-1">
        <li>• Breakage, stain & fragment inspection</li>
        <li>• Pinhole, watermark & blade line</li>
        <li>• Defect detection across all grades</li>
      </ul>
    </div>

    {/* PCB */}
    <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-black p-7 rounded-2xl border border-gray-700 hover:shadow-[0_0_20px_rgba(255,255,255,0.08)] transition-all duration-500">
      <h3 className="text-xl font-semibold mb-3">PCB & Flexible Boards</h3>
      <ul className="text-gray-400 text-sm space-y-1">
        <li>• AOI for rigid & flexible PCBs</li>
      </ul>
    </div>

    {/* Copper Foil */}
    <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-black p-7 rounded-2xl border border-gray-700 hover:shadow-[0_0_20px_rgba(255,255,255,0.08)] transition-all duration-500">
      <h3 className="text-xl font-semibold mb-3">Copper Foil & Substrate</h3>
      <ul className="text-gray-400 text-sm space-y-1">
        <li>• Surface defect inspection</li>
        <li>• Pinhole detection</li>
      </ul>
    </div>

    {/* Photoelectric */}
    <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-black p-7 rounded-2xl border border-gray-700 hover:shadow-[0_0_20px_rgba(255,255,255,0.08)] transition-all duration-500">
      <h3 className="text-xl font-semibold mb-3">Photoelectric Panels</h3>
      <ul className="text-gray-400 text-sm space-y-1">
        <li>• Breakage inspection machines</li>
        <li>• ITO coating inspection</li>
      </ul>
    </div>

    {/* Textile & Fiber */}
    <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-black p-7 rounded-2xl border border-gray-700 hover:shadow-[0_0_20px_rgba(255,255,255,0.08)] transition-all duration-500">
      <h3 className="text-xl font-semibold mb-3">Textile & Fiber</h3>
      <ul className="text-gray-400 text-sm space-y-1">
        <li>• Automatic & AI fabric inspection</li>
        <li>• Fiber stain & defect inspection</li>
      </ul>
    </div>

    {/* Plastic Films */}
    <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-black p-7 rounded-2xl border border-gray-700 hover:shadow-[0_0_20px_rgba(255,255,255,0.08)] transition-all duration-500">
      <h3 className="text-xl font-semibold mb-3">Plastic Films & Converting</h3>
      <ul className="text-gray-400 text-sm space-y-1">
        <li>• Cast, blown, biax & hygiene films</li>
        <li>• GSM, coat weight & multilayer thickness</li>
        <li>• Coating & thickness measurement</li>
      </ul>
    </div>

  </div>
</section>


        {/* --- CONTACT US --- */}
<div className="bg-gradient-to-r from-gray-900 via-gray-800 to-black mt-16 rounded-2xl p-10 shadow-2xl text-white border border-gray-700 transition-transform duration-500 hover:scale-[1.02] hover:shadow-[0_0_20px_rgba(255,255,255,0.1)]">
  <h2 className="text-3xl font-bold text-center mb-6 tracking-wide bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
    CONTACT US
  </h2>

  <div className="grid grid-cols-1 md:grid-cols-2 gap-10 text-center md:text-left">
    {/* Ram Bomble */}
    <div className="p-6 rounded-xl bg-gray-800/60 border border-gray-700 hover:bg-gray-800/80 transition duration-300">
      <h3 className="text-2xl font-semibold mb-2">Ram Bomble</h3>
      <p className="text-gray-400 mb-1">Reach us at:</p>
      <a
        href="mailto:rambomble@alphaq.co.in"
        className="block text-gray-300 hover:text-white font-medium transition-colors"
      >
        rambomble@alphaq.co.in
      </a>
      <p className="text-gray-400 mt-2">Phone: +91 9879511250</p>
    </div>

    {/* Sanskar Bomble */}
    <div className="p-6 rounded-xl bg-gray-800/60 border border-gray-700 hover:bg-gray-800/80 transition duration-300">
      <h3 className="text-2xl font-semibold mb-2">Sanskar Bomble</h3>
      <p className="text-gray-400 mb-1">Reach us at:</p>
      <a
        href="mailto:alphaqsanskar@gmail.com"
        className="block text-gray-300 hover:text-white font-medium transition-colors"
      >
        alphaqsanskar@gmail.com
      </a>
      <p className="text-gray-400 mt-2">Phone: +91 9867697135</p>
    </div>
  </div>

  {/* Divider line */}
  <div className="mt-10 border-t border-gray-700 opacity-50"></div>

  {/* Footer text */}
  <p className="text-center text-gray-400 mt-6 text-sm">
    We’re always excited to connect — let’s build something amazing together.
  </p>
</div>

      </div>
    </section>
  );
};

export default AboutUs;
